/**
 * Canonical registry of configurable action-list buttons per entity.
 * Used by PageAndActionAccessList (role editor) and useActionRight (runtime checks).
 */
export const ACTION_ACCESS_HIDDEN = 0;
export const ACTION_ACCESS_DISABLED = 1;
export const ACTION_ACCESS_ACCESSIBLE = 15;

export const actionAccessOptions = [
  { value: ACTION_ACCESS_ACCESSIBLE, label: "Accessible", color: "green" },
  { value: ACTION_ACCESS_DISABLED, label: "Disable", color: "orange" },
  { value: ACTION_ACCESS_HIDDEN, label: "Hidden", color: "red" },
];

export const actionRegistry = [
  {
    entity: "order",
    label: "Order",
    pageLinkIds: ["O2", "GI2", "M102"],
    actions: [
      { key: "addRider", label: "Add New Rider" },
      { key: "editOrder", label: "Edit Order Details" },
      { key: "editOrderConsultant", label: "Edit Order Details (Consultant)" },
      { key: "adjustOrder", label: "Adjust Order" },
      { key: "printReceiveCoverSheet", label: "Receive / Print Cover Sheet" },
      { key: "printSubmitCoverSheet", label: "Print Submit Cover Sheet" },
      { key: "editPolicy", label: "Edit Policy" },
      { key: "confirmPolicy", label: "Confirm Policy" },
      { key: "deliverPolicy", label: "Deliver Policy" },
      { key: "createTicket", label: "Create Ticket" },
      { key: "terminatePolicy", label: "Terminate Policy" },
      { key: "cancelOrder", label: "Cancel Order" },
      { key: "redoCancelOrder", label: "Redo Cancel Order" },
      { key: "reinstantiateOrder", label: "Reinstantiate Order" },
    ],
  },
  {
    entity: "debitNote",
    label: "Debit Note",
    pageLinkIds: ["GIDEB", "GIDEB-M"],
    actions: [
      { key: "printPdf", label: "Print Debit Note" },
      { key: "printExcel", label: "Download" },
      { key: "printReceipt", label: "Print Receipt" },
      { key: "editDebitNote", label: "Edit Debit Note" },
      { key: "adjustDebitNote", label: "Adjust Debit Note" },
      { key: "createTicket", label: "Create Ticket" },
      { key: "confirmPolicy", label: "Confirm Policy" },
      { key: "unsettlePayment", label: "Unsettle Payment" },
      { key: "terminate", label: "Terminate" },
    ],
  },
  {
    entity: "quotation",
    label: "Quotation",
    pageLinkIds: ["GIQuo", "GIQuo-M"],
    actions: [
      { key: "printQuotation", label: "Quotation Print" },
      { key: "confirmQuotation", label: "Confirm Quotation" },
      { key: "copyToDebitNote", label: "Confirm & Generate Debit Note" },
      { key: "cancelQuotation", label: "Cancel" },
    ],
  },
  {
    entity: "renewal",
    label: "Renewal",
    pageLinkIds: ["GIRen", "GIRen-M"],
    actions: [
      { key: "printPdf", label: "Download Renewal Notice (PDF)" },
      { key: "printWord", label: "Download Renewal Notice (Word)" },
      { key: "confirmRenewal", label: "Confirm Renewal" },
      { key: "receiveRenewal", label: "Receive Renewal" },
      { key: "deliverRenewal", label: "Deliver Renewal" },
      { key: "printCancelNotice", label: "Print Cancel Notice" },
      { key: "rejectRenewal", label: "Reject / Terminate Renewal" },
    ],
  },
  {
    entity: "quoRequest",
    label: "Quo Request",
    pageLinkIds: ["GIQUOREQ"],
    actions: [
      { key: "editRequest", label: "Edit Request" },
      { key: "editRequestConsultant", label: "Edit Request (Consultant)" },
      { key: "createTicket", label: "Create Ticket" },
      { key: "completeRequest", label: "Complete Request" },
      { key: "confirmQuotation", label: "Confirm Quotation" },
      { key: "copyToDebitNote", label: "Copy to Debit Note" },
      { key: "cancelRequest", label: "Cancel Request" },
      { key: "reactiveRequest", label: "Reactive Request" },
    ],
  },
  {
    entity: "orderService",
    label: "Order Service",
    pageLinkIds: ["O12", "M110", "C15"],
    actions: [
      { key: "printCoverSheet", label: "Print Cover Sheet" },
      { key: "receive", label: "Receive" },
      { key: "printSubmitCoverSheet", label: "Print Submit Cover Sheet" },
      { key: "cancel", label: "Cancel" },
      { key: "complete", label: "Complete" },
    ],
  },
  {
    entity: "client",
    label: "Client",
    pageLinkIds: ["O1", "GI1", "M101"],
    actions: [
      { key: "updateClient", label: "Update Client" },
      { key: "createTicket", label: "Create Ticket" },
      { key: "enableActive", label: "Enable / Active" },
      { key: "changeServiceRight", label: "Change Service Right" },
      { key: "statement", label: "Client Statement" },
      { key: "policyReview", label: "Policy Review" },
      { key: "transferCredit", label: "Transfer Credit" },
      { key: "refundCredit", label: "Refund Credit" },
    ],
  },
  {
    entity: "user",
    label: "User",
    pageLinkIds: ["O7", "M107", "A4", "GI7"],
    actions: [
      { key: "editUser", label: "Edit User" },
      { key: "setPassword", label: "Set Password" },
      { key: "disableCreateOrder", label: "Disable Create Order" },
      { key: "activeEnable", label: "Active / Enable" },
      { key: "walletActive", label: "Wallet Active" },
      { key: "leaveAction", label: "Leave Action" },
      { key: "transferClient", label: "Transfer Client" },
    ],
  },
  {
    entity: "allowance",
    label: "Allowance",
    pageLinkIds: ["M1", "A10"],
    actions: [
      { key: "terminate", label: "Terminate Allowance" },
      { key: "reactive", label: "Reactive Allowance" },
    ],
  },
  {
    entity: "creditNote",
    label: "Credit Note",
    pageLinkIds: [],
    actions: [
      { key: "printPdf", label: "Print Credit Note" },
      { key: "printExcel", label: "Download Excel" },
      { key: "editCreditNote", label: "Edit Credit Note" },
      { key: "settle", label: "Settle" },
      { key: "unsettle", label: "Unsettle" },
      { key: "delivery", label: "Delivery" },
      { key: "terminate", label: "Terminate" },
    ],
  },
];

const buildPageLinkIdToEntities = () => {
  const map = {};
  actionRegistry.forEach(({ entity, pageLinkIds }) => {
    (pageLinkIds ?? []).forEach((linkId) => {
      if (!map[linkId]) map[linkId] = [];
      if (!map[linkId].includes(entity)) map[linkId].push(entity);
    });
  });
  return map;
};

export const pageLinkIdToEntities = buildPageLinkIdToEntities();

export const getEntitiesForPageLink = (linkId) =>
  pageLinkIdToEntities[linkId] ?? [];

export const getRegistryEntry = (entity) =>
  actionRegistry.find((e) => e.entity === entity);

export const getAllActionKeys = () =>
  actionRegistry.flatMap(({ entity, actions }) =>
    actions.map(({ key }) => ({ entity, action: key }))
  );

export const getEntityActions = (entity) =>
  actionRegistry.find((e) => e.entity === entity)?.actions ?? [];
